import { addNavbarNotification } from "../utils/navbarNotifications";
import {
  loadAttendanceGeneralSettings,
  ATTENDANCE_GENERAL_SETTINGS_UPDATED_EVENT,
  ATTENDANCE_SCHEDULE_RECALC_EVENT,
  dispatchAttendanceScheduleRecalc,
  getScheduleIntervalMs,
  scheduleLabel,
  setScheduleLastRunMs,
  shouldRunScheduledRecalc,
} from "./attendanceGeneralSettings";
import { ATTENDANCE_PUNCH_UPDATED_EVENT } from "./attendancePunchStorage";
import { startDeviceIntegrationPoller } from "./attendanceDeviceIntegration";

const TICK_MS = 60 * 1000;

let tickTimer = null;

function notifyScheduleRecalc(settings, { reason = "scheduled" } = {}) {
  const roles = settings?.notifyRoles || [];
  const label = scheduleLabel(settings?.runAttendanceSchedule);
  if (roles.length) {
    addNavbarNotification({
      targetRoles: roles,
      title: "Attendance recalculated",
      message: `PR/LOP totals were refreshed (${label} schedule${reason === "manual" ? ", saved settings" : ""}).`,
      type: "attendance_schedule",
    });
  }
}

export function runAttendanceScheduleRecalc({ reason = "scheduled", notify = true } = {}) {
  const settings = loadAttendanceGeneralSettings();
  setScheduleLastRunMs();
  dispatchAttendanceScheduleRecalc({ reason, schedule: settings.runAttendanceSchedule });
  if (notify) notifyScheduleRecalc(settings, { reason });
}

let runnerStartedFlag = false;

/**
 * Global timer: non-realtime schedules fire batch PR/LOP recalc; realtime relies on punch events.
 */
export function startAttendanceScheduleRunner() {
  if (typeof window === "undefined" || runnerStartedFlag) return () => {};
  runnerStartedFlag = true;

  const onPunch = () => {
    const { runAttendanceSchedule } = loadAttendanceGeneralSettings();
    if (runAttendanceSchedule === "realtime") {
      dispatchAttendanceScheduleRecalc({ reason: "punch", batch: false });
    }
  };

  const onSettingsSaved = (e) => {
    const schedule = e?.detail?.runAttendanceSchedule;
    if (schedule && schedule !== "realtime") {
      runAttendanceScheduleRecalc({ reason: "manual", notify: true });
    }
  };

  const tick = () => {
    const settings = loadAttendanceGeneralSettings();
    const { runAttendanceSchedule } = settings;
    if (runAttendanceSchedule === "realtime") return;
    if (shouldRunScheduledRecalc(runAttendanceSchedule)) {
      runAttendanceScheduleRecalc({ reason: "scheduled", notify: true });
    }
  };

  window.addEventListener(ATTENDANCE_PUNCH_UPDATED_EVENT, onPunch);
  window.addEventListener(ATTENDANCE_GENERAL_SETTINGS_UPDATED_EVENT, onSettingsSaved);
  tickTimer = window.setInterval(tick, TICK_MS);
  tick();
  const stopDevices = startDeviceIntegrationPoller();

  return () => {
    if (stopDevices) stopDevices();
    runnerStartedFlag = false;
    window.removeEventListener(ATTENDANCE_PUNCH_UPDATED_EVENT, onPunch);
    window.removeEventListener(
      ATTENDANCE_GENERAL_SETTINGS_UPDATED_EVENT,
      onSettingsSaved
    );
    if (tickTimer) window.clearInterval(tickTimer);
    tickTimer = null;
  };
}

export { ATTENDANCE_SCHEDULE_RECALC_EVENT, getScheduleIntervalMs };
